import { Pipe, PipeTransform } from '@angular/core';
import { Field, FieldDataType } from '@app/models/module-structure';
import * as accounting from 'accounting';
import * as moment from 'moment';
import * as g from 'src/app/globals';
import { RecordLine, RecordProperties } from './record-model';

@Pipe({
  name: 'valueRenderer',
})
export class ValueRendererPipe implements PipeTransform {
  transform(
    value: any,
    recordProperty: RecordProperties,
    field?: Field,
    recordLine?: RecordLine,
    _optionsLength?: number,
  ): string {
    if (recordProperty == null) {
      return '';
    }

    const dataType = recordProperty.DataType ?? field?.DataType;
    switch (dataType) {
      case FieldDataType.Number:
      case FieldDataType.TimeAmount:
        return this.formatNumber(
          value,
          g.companyConfiguration.PriceDecimal,
        );
      case FieldDataType.Select:
        return this.formatSelect(value, recordProperty);
      case FieldDataType.Date:
      case FieldDataType.MultipleDate:
        return value
          ? moment(value).format(g.companyConfiguration.MomentDateFormat)
          : '';
      case FieldDataType.Price:
      case FieldDataType.PriceLocalCurrency:
      case FieldDataType.PriceSystemCurrency:
      case FieldDataType.PriceFromDocument:
        return this.formatNumber(
          value,
          g.companyConfiguration.PriceDecimal,
        );
      case FieldDataType.PriceLines:
        return this.formatPriceWithCurrency(value, recordLine);
      case FieldDataType.Percent:
        if (value == null || isNaN(Number(value))) {
          value = 0;
        }
        return accounting.formatMoney(
          Number(value),
          '%',
          g.companyConfiguration.PercentDecimal,
          '',
          g.companyConfiguration.DecimalSep,
          '%v %s',
        );
      case FieldDataType.Time:
        return this.formatTime(value);
      case FieldDataType.Checkbox:
        return value === field?.AdditionalInformationParsed?.ValueYes
          ? '☑'
          : '☐';
      case FieldDataType.Amount:
      case FieldDataType.AmountFromDocument:
        return this.formatNumber(
          value,
          g.companyConfiguration.AmountDecimal,
          true,
        );
      case FieldDataType.Quantity:
        return this.formatNumber(
          value,
          g.companyConfiguration.QuantityDecimal,
          true,
        );
      case FieldDataType.Integer:
        return value == null || isNaN(Number(value))
          ? '0'
          : Number(value).toFixed(0);
      case FieldDataType.Rate:
        return this.formatNumber(
          value,
          g.companyConfiguration.RateDecimal,
          true,
        );
      case FieldDataType.ItemImage:
      case FieldDataType.Signature:
      case FieldDataType.Icon:
      case FieldDataType.Button:
        return '';
      default:
        return this.toDisplayString(value);
    }
  }

  private formatNumber(value: any, decimals: number, nullAsZero = false): string {
    if (value == null || value === '' || isNaN(Number(value))) {
      if (!nullAsZero) {
        return '';
      }
      value = 0;
    }

    return accounting.formatNumber(
      Number(value),
      decimals,
      g.companyConfiguration.ThousandSep,
      g.companyConfiguration.DecimalSep,
    );
  }

  private formatSelect(
    value: any,
    recordProperty: RecordProperties,
  ): string {
    if (value == null || value === '') {
      return '';
    }

    const values = Array.isArray(value) ? value : value.toString().split(',');
    const options = Array.isArray(recordProperty.Options)
      ? recordProperty.Options
      : [];
    return values
      .map((selectedValue) => {
        const option = options.find(
          (candidate) =>
            candidate.Code == selectedValue ||
            candidate.DBValue == selectedValue,
        );
        return option?.Name ?? selectedValue;
      })
      .join(', ');
  }

  private formatPriceWithCurrency(
    value: any,
    recordLine?: RecordLine,
  ): string {
    const formattedValue = this.formatNumber(
      value,
      g.companyConfiguration.PriceDecimal,
    );
    if (formattedValue === '') {
      return '';
    }

    const currency = recordLine?.Properties?.find(
      (property) =>
        property.Name === 'Currency' || property.Name === 'PriceCurrency',
    )?.ValueAsObject;
    if (currency == null || currency === '') {
      return formattedValue;
    }

    return g.companyConfiguration.CurrencyOnRight
      ? `${formattedValue} ${currency}`
      : `${currency} ${formattedValue}`;
  }

  private formatTime(value: any): string {
    if (value == null || value === '') {
      return '';
    }

    const timeFormat =
      g.companyConfiguration.TimeFormat === 'hh:mmtt'
        ? 'hh:mma'
        : g.companyConfiguration.TimeFormat;
    const parsedValue = moment(value);
    return parsedValue.isValid()
      ? parsedValue.format(timeFormat)
      : this.toDisplayString(value);
  }

  private toDisplayString(value: any): string {
    return value == null ? '' : value.toString();
  }
}
